import { useEffect, useRef, useState } from 'react'
import { useIsMutating } from '@tanstack/react-query'
import { Alert, Box, Stack } from '@mui/material'
import ModalDialog from '../modal-dialog/ModalDialog'
import {
    resolveCompanyOptions,
    resolveRecipientOptions,
    resolveSelectedSenderDetailId,
    resolveSenderNameOptions,
    resolveShareableContactIds,
    resolveChosenContactOptions,
} from '../../data-organizers/announcements/resolveSendOptions'
import useAnnouncementSendContext from './useAnnouncementSendContext'
import useOptimisticList from '../contacts/useOptimisticList'
import { detailFieldLabelOf } from '../contacts/contactLabels'
import { toDetailBody } from '../contacts/contactLists'
import { useNotificationContext } from 'units/notifications/NotificationContext'
import { messageOf } from '../Message'
import SendStepper from './SendStepper'
import SendCompanyStep from './SendCompanyStep'
import SendRecipientStep from './SendRecipientStep'
import SendSenderStep from './SendSenderStep'
import SendContactsStep from './SendContactsStep'
import SendConfirmationStep from './SendConfirmationStep'
import PendingDetailsDialog from './PendingDetailsDialog'

export { COMPANY_HEADER, COMPANY_LABEL } from './SendCompanyStep'

export {
    EMAILS_HEADER,
    NO_EMAILS_MESSAGE,
    NO_RECIPIENTS_MESSAGE,
} from './SendRecipientStep'

export { SENDER_HEADER, SENDER_NAMES_READ_ONLY_MESSAGE } from './SendSenderStep'

export {
    CONTACTS_HEADER,
    CONTACTS_READ_ONLY_MESSAGE,
    NO_CONTACTS_MESSAGE,
    NO_SHAREABLE_CONTACTS_MESSAGE,
} from './SendContactsStep'

export {
    SUMMARY_COMPANY_HEADER,
    SUMMARY_CHANGE_LABEL,
    MISSING_COMPANY_LINK,
    MISSING_COMPANY_MESSAGE,
    SUMMARY_RECIPIENTS_HEADER,
    SUMMARY_SENDER_HEADER,
    MISSING_RECIPIENTS_LINK,
    MISSING_RECIPIENTS_MESSAGE,
    MISSING_SENDER_LINK,
    MISSING_SENDER_MESSAGE,
} from './SendConfirmationStep'

export {
    LEAVE_CANCEL_LABEL,
    LEAVE_DISCARD_LABEL,
    LEAVE_SAVE_LABEL,
    LEAVE_TITLE,
} from './PendingDetailsDialog'

export const SEND_LABEL = 'שליחה'

export const CANCEL_LABEL = 'ביטול'

export const PREVIOUS_LABEL = 'הקודם'

export const NEXT_LABEL = 'הבא'

export const TITLE = 'שליחת המודעה ללקוח'

export const NO_FIRM_MESSAGE = 'החשבון שלך אינו משויך למשרד, ולכן לא ניתן לשלוח את המודעה. נא לפנות לתמיכה.'

const COMPANY = 0

const RECIPIENTS = 1

const SENDER = 2

const CONTACTS = 3

const CONFIRMATION = 4

const STEP_LABELS = ['חברה', 'נמענים', 'שם השולח', 'פרטי קשר', 'אישור ושליחה']

const COMPANY_DETAILS_LENS = {
    get: (cached) => cached?.data?.contact?.details ?? [],
    set: (cached, details) => ({
        ...cached,
        data: {
            ...cached.data,
            contact: {
                ...cached.data?.contact,
                details,
            },
        },
    }),
}

const FIRM_DETAILS_LENS = {
    get: (cached) => cached?.data ?? [],
    set: (cached, names) => ({
        ...cached,
        data: names,
    }),
}

const DIALOG_TOP_OFFSET = '20vh'

const DIALOG_CONTAINER = {
    alignItems: 'flex-start',
}

const DIALOG_PAPER = {
    maxWidth: 700,
    height: '60vh',
    minHeight: `min(550px, calc(100% - ${DIALOG_TOP_OFFSET} - 32px))`,
    maxHeight: `calc(100% - ${DIALOG_TOP_OFFSET} - 32px)`,
    marginTop: DIALOG_TOP_OFFSET,
}

const DIALOG_CONTENT = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflowY: 'hidden',
}

const STEPPER_AREA = {
    flexShrink: 0,
}

const STEP_SCROLL_AREA = {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    '@supports not selector(::-webkit-scrollbar)': {
        scrollbarWidth: 'thin',
        scrollbarColor: ({ palette }) => `${palette.grey[400]} transparent`,
    },
    '&::-webkit-scrollbar': {
        width: 8,
        height: 8,
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: ({ palette }) => palette.grey[400],
        borderRadius: 4,
    },
}

export default function AnnouncementSendForm({
    announcement,
    busy = false,
    onSend,
    onClose,
}) {
    const sendContext = useAnnouncementSendContext(announcement)

    return (
        <AnnouncementSendFormView
            {...sendContext}
            busy={busy}
            onSend={(request) => onSend?.(() => sendContext.onSend(request))}
            onClose={onClose}
        />
    )
}

function AnnouncementSendFormView({
    firm,
    companies,
    companyDetails,
    recipientsResolved,
    senderNames,
    senderNamesEditable,
    contactDetails,
    contactDetailsResolved,
    contactDetailsEditable,
    selectedCompanyId,
    companyQueryKey,
    senderNamesQueryKey,
    contactDetailsQueryKey,
    rememberedSenderDetailId,
    busy,
    onCompanySelected,
    onDetailsChanged,
    onSend,
    onClose,
}) {
    const belongsToFirm = firm != null

    const [activeStep, setActiveStep] = useState(COMPANY)
    const runningRequests = useIsMutating()

    const recipientOptions = belongsToFirm ? resolveRecipientOptions(companyDetails) : []
    const sendableIds = recipientOptions.map((option) => option.id)
    const senderNameOptions = resolveSenderNameOptions(senderNames)

    const { addNotification } = useNotificationContext()

    const notifyRefusal = (error) => {
        addNotification({
            type: 'error',
            content: messageOf(error),
        })
    }

    const emails = useOptimisticList({
        queryKey: companyQueryKey,
        lens: COMPANY_DETAILS_LENS,
        path: `/companies/${selectedCompanyId}/details`,
        scope: `company-${selectedCompanyId}:details`,
        toInput: toDetailBody,
        labelOf: detailFieldLabelOf,
        onRefused: notifyRefusal,
        onStored: onDetailsChanged,
    })

    const names = useOptimisticList({
        queryKey: senderNamesQueryKey,
        lens: FIRM_DETAILS_LENS,
        path: '/users/me/firm/settings/details',
        scope: 'own-firm:details',
        toInput: toDetailBody,
        labelOf: detailFieldLabelOf,
        onRefused: notifyRefusal,
    })

    const contacts = useOptimisticList({
        queryKey: contactDetailsQueryKey,
        lens: FIRM_DETAILS_LENS,
        path: '/users/me/firm/settings/details',
        scope: 'own-firm:details',
        toInput: toDetailBody,
        labelOf: detailFieldLabelOf,
        onRefused: notifyRefusal,
    })

    const [chosenRecipientIds, setChosenRecipientIds] = useState([])
    const [chosenSenderDetailId, setChosenSenderDetailId] = useState(null)
    const [senderCleared, setSenderCleared] = useState(false)
    const resolvedSenderDetailId = resolveSelectedSenderDetailId(
        senderNameOptions,
        chosenSenderDetailId ?? rememberedSenderDetailId,
    )
    const senderDetailId = senderCleared ? null : resolvedSenderDetailId
    const previousSenderDetailId = useRef(null)
    const senderOptionsKey = senderNameOptions.map((option) => option.id).join(',')

    useEffect(() => {
        const previous = previousSenderDetailId.current
        const stillOffered = senderNameOptions.some((option) => option.id === previous)

        if (previous != null && !stillOffered) {
            setSenderCleared(true)
        }

        previousSenderDetailId.current = senderDetailId
    }, [senderOptionsKey, senderDetailId])

    const selectSender = (id) => {
        setSenderCleared(false)
        setChosenSenderDetailId(id)
    }

    const storedEmailKey = emails.items.map((detail) => detail.id).join(',')

    useEffect(() => {
        const storedIds = emails.items.map((detail) => detail.id)
        setChosenRecipientIds((chosen) => {
            const kept = chosen.filter((id) => storedIds.includes(id))
            return kept.length === chosen.length ? chosen : kept
        })
    }, [storedEmailKey])

    useEffect(() => {
        const reportedIds = (companyDetails ?? []).map((detail) => detail.id)
        setChosenRecipientIds((chosen) => {
            const kept = chosen.filter((id) => reportedIds.includes(id))
            return kept.length === chosen.length ? chosen : kept
        })
    }, [companyDetails])

    const [approvedIds, setApprovedIds] = useState([])
    const sendableKey = sendableIds.join(',')

    useEffect(() => {
        const nowSendable = approvedIds.filter((id) => sendableIds.includes(id))
        if (nowSendable.length === 0) {
            return
        }

        setApprovedIds((approved) => approved.filter((id) => !nowSendable.includes(id)))
        setChosenRecipientIds((chosen) => [
            ...chosen,
            ...nowSendable.filter((id) => !chosen.includes(id)),
        ])
    }, [approvedIds, sendableKey])

    const markApproved = (id) => {
        setApprovedIds((approved) => [...approved, id])
    }

    const [selectionCount, setSelectionCount] = useState(0)
    const seededSelection = useRef(null)

    useEffect(() => {
        const alreadySeeded = seededSelection.current === selectionCount
        if (!recipientsResolved || alreadySeeded) {
            return
        }

        seededSelection.current = selectionCount

        if (sendableIds.length === 1) {
            setChosenRecipientIds(sendableIds)
        }
    }, [selectionCount, recipientsResolved, sendableKey])

    const selectCompany = (companyId) => {
        setSelectionCount((count) => count + 1)
        setChosenRecipientIds([])
        setApprovedIds([])
        onCompanySelected?.(companyId)
    }

    const toggleRecipient = (id) => {
        setChosenRecipientIds((chosen) => (
            chosen.includes(id)
                ? chosen.filter((one) => one !== id)
                : [...chosen, id]
        ))
    }

    const [chosenContactIds, setChosenContactIds] = useState([])
    const [approvedContactIds, setApprovedContactIds] = useState([])
    const seededContacts = useRef(false)
    const shareableContactIds = resolveShareableContactIds(contactDetails)
    const shareableContactKey = shareableContactIds.join(',')

    useEffect(() => {
        if (!contactDetailsResolved || seededContacts.current) {
            return
        }

        seededContacts.current = true
        setChosenContactIds(shareableContactIds)
    }, [contactDetailsResolved, shareableContactKey])

    useEffect(() => {
        setChosenContactIds((chosen) => {
            const kept = chosen.filter((id) => shareableContactIds.includes(id))
            return kept.length === chosen.length ? chosen : kept
        })
    }, [shareableContactKey])

    useEffect(() => {
        const nowShareable = approvedContactIds.filter((id) => shareableContactIds.includes(id))
        if (nowShareable.length === 0) {
            return
        }

        setApprovedContactIds((approved) => approved.filter((id) => !nowShareable.includes(id)))
        setChosenContactIds((chosen) => [
            ...chosen,
            ...nowShareable.filter((id) => !chosen.includes(id)),
        ])
    }, [approvedContactIds, shareableContactKey])

    const markContactApproved = (id) => {
        setApprovedContactIds((approved) => [...approved, id])
    }

    const toggleContact = (id) => {
        setChosenContactIds((chosen) => (
            chosen.includes(id)
                ? chosen.filter((one) => one !== id)
                : [...chosen, id]
        ))
    }

    const [pendingLeave, setPendingLeave] = useState(null)
    const activeDetails = [null, emails, names, contacts, null][activeStep]

    const requestLeave = (next) => {
        if ((activeDetails?.removals.length ?? 0) > 0) {
            setPendingLeave(() => next)
            return
        }

        next()
    }

    const saveAndLeave = async () => {
        const next = pendingLeave
        setPendingLeave(null)

        const removedIds = [...activeDetails.removals]
        const confirmations = removedIds.map((id) => activeDetails.confirmRemoval(id))
        const outcomes = await Promise.all(confirmations)
        const refused = outcomes.some((outcome) => outcome.refused != null)

        if (!refused) {
            next({ storedRemovals: removedIds })
        }
    }

    const discardAndLeave = () => {
        const next = pendingLeave
        setPendingLeave(null)

        activeDetails.removals.forEach((id) => activeDetails.restore(id))
        next()
    }

    const goTo = (step) => requestLeave(() => setActiveStep(step))

    const close = () => requestLeave(() => onClose?.())

    const chosenRecipients = recipientOptions
        .filter((option) => chosenRecipientIds.includes(option.id))
    const chosenContacts = resolveChosenContactOptions(contacts.items, chosenContactIds)
    const companyName = resolveCompanyOptions(companies)
        .find((option) => option.id === selectedCompanyId)
        ?.label
    const senderName = senderNameOptions
        .find((option) => option.id === senderDetailId)
        ?.value

    const completed = [
        selectedCompanyId != null,
        chosenRecipients.length > 0,
        senderDetailId != null,
        chosenContactIds.length > 0,
    ]

    const requiredCompleted = completed.slice(0, 3)

    const canSend = belongsToFirm
        && !busy
        && runningRequests === 0
        && requiredCompleted.every(Boolean)

    const deliver = ({ storedRemovals = [] } = {}) => {
        const removedRecipients = activeStep === RECIPIENTS ? storedRemovals : []
        const removedSenders = activeStep === SENDER ? storedRemovals : []
        const remaining = chosenRecipients
            .filter((recipient) => !removedRecipients.includes(recipient.id))
        const senderRemoved = removedSenders.includes(senderDetailId)

        if (remaining.length === 0 || senderRemoved) {
            return
        }

        onSend?.({
            companyId: selectedCompanyId,
            recipientIds: remaining.map((recipient) => recipient.id),
            recipients: remaining.map((recipient) => recipient.value),
            senderDetailId,
            contactDetailIds: chosenContactIds,
        })
    }

    const cancelCommand = {
        label: CANCEL_LABEL,
        testId: 'send-cancel',
        position: 'end',
        color: 'secondary',
        disabled: busy,
        onClick: close,
    }
    const sendCommand = {
        label: SEND_LABEL,
        testId: 'send-submit',
        position: 'end',
        color: 'primary',
        disabled: !canSend,
        onClick: () => requestLeave(deliver),
    }
    const previousCommand = {
        label: PREVIOUS_LABEL,
        testId: 'send-previous',
        color: 'info',
        disabled: busy || activeStep === COMPANY,
        onClick: () => goTo(activeStep - 1),
    }
    const nextCommand = {
        label: NEXT_LABEL,
        testId: 'send-next',
        color: 'info',
        disabled: busy || activeStep === CONFIRMATION,
        onClick: () => goTo(activeStep + 1),
    }
    const commands = [previousCommand, nextCommand, cancelCommand, sendCommand]

    return (
        <ModalDialog
            open
            title={TITLE}
            commands={commands}
            paperSx={DIALOG_PAPER}
            containerSx={DIALOG_CONTAINER}
            contentSx={DIALOG_CONTENT}
            onClose={close}
        >
            <Box sx={STEPPER_AREA}>
                <SendStepper
                    labels={STEP_LABELS}
                    activeStep={activeStep}
                    completed={completed}
                    onStep={goTo}
                />
            </Box>

            <Box sx={STEP_SCROLL_AREA}>
                <Stack spacing={2}>
                    {!belongsToFirm && <Alert severity="warning" data-testid="send-no-firm">{NO_FIRM_MESSAGE}</Alert>}

                    {activeStep === COMPANY && (
                        <SendCompanyStep
                            companies={companies}
                            selectedCompanyId={selectedCompanyId}
                            onCompanySelected={selectCompany}
                        />
                    )}

                    {activeStep === RECIPIENTS && (
                        <SendRecipientStep
                            belongsToFirm={belongsToFirm}
                            selectedCompanyId={selectedCompanyId}
                            onGoToCompany={() => goTo(COMPANY)}
                            emails={emails}
                            onDetailsChanged={onDetailsChanged}
                            sendableIds={sendableIds}
                            selectedIds={chosenRecipientIds}
                            onToggle={toggleRecipient}
                            onApproved={markApproved}
                        />
                    )}

                    {activeStep === SENDER && (
                        <SendSenderStep
                            names={names}
                            editable={senderNamesEditable}
                            selectableIds={senderNameOptions.map((option) => option.id)}
                            selectedId={senderDetailId}
                            onSelect={selectSender}
                        />
                    )}

                    {activeStep === CONTACTS && (
                        <SendContactsStep
                            contacts={contacts}
                            editable={contactDetailsEditable}
                            shareableIds={shareableContactIds}
                            selectedIds={chosenContactIds}
                            onToggle={toggleContact}
                            onApproved={markContactApproved}
                        />
                    )}

                    {activeStep === CONFIRMATION && (
                        <SendConfirmationStep
                            companyName={companyName}
                            recipients={chosenRecipients}
                            senderName={senderName}
                            contacts={chosenContacts}
                            onGoToContacts={() => goTo(CONTACTS)}
                            onGoToCompany={() => goTo(COMPANY)}
                            onGoToRecipients={() => goTo(RECIPIENTS)}
                            onFollowMissingRecipients={() => goTo(selectedCompanyId == null ? COMPANY : RECIPIENTS)}
                            onGoToSender={() => goTo(SENDER)}
                        />
                    )}
                </Stack>
            </Box>

            <PendingDetailsDialog
                open={pendingLeave != null}
                onSave={saveAndLeave}
                onDiscard={discardAndLeave}
                onCancel={() => setPendingLeave(null)}
            />
        </ModalDialog>
    )
}
